'use client'

import Link from 'next/link'
import { MapPin, Clock } from 'lucide-react'
import { formatBudgetRange } from '@/lib/jobs/budget-formatter'
import { SPECIALIZATIONS } from '@/config/specializations'
import { DeadlineBadge } from './deadline-badge'
import { JobTypeBadge } from './job-type-badge'
import { SaveJobButton } from './save-job-button'
import { VerificationBadge } from '@/app/(app)/company/[slug]/_components/verification-badge'
import type { JobCardData } from '@/types/api.types'

interface JobCardProps {
  job: JobCardData
  isAuthenticated?: boolean
}

function getSpecLabel(value: string): string {
  return SPECIALIZATIONS.find((s) => s.value === value)?.label ?? value
}

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const minutes = Math.floor(diff / 60_000)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}


export function JobCard({ job, isAuthenticated = true }: JobCardProps) {
  const font = { fontFamily: 'var(--font-inter, Inter, sans-serif)' }
  const locationLabel = job.locationType === 'remote' ? 'Remote' : job.location || job.locationType

  return (
    <Link
      href={isAuthenticated ? `/jobs/${job.slug}` : '/login'}
      className="group block rounded-[var(--card-radius)] border border-[var(--card-border)] bg-[var(--card-bg)] p-5 shadow-[var(--card-shadow)] transition-shadow hover:shadow-[var(--shadow-md)]"
    >
      <div className="flex items-start gap-3">
        {/* Company logo */}
        {job.company.logoUrl ? (
          <img
            src={job.company.logoUrl}
            alt={`${job.company.name} logo`}
            className="h-10 w-10 rounded-lg object-cover"
          />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--color-bg-elevated)] text-sm font-bold text-[var(--color-text-muted)]">
            {job.company.name.charAt(0).toUpperCase()}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <h3
            className="truncate text-sm font-semibold text-[var(--color-text-primary)] group-hover:text-[var(--color-brand-indigo)]"
            style={font}
          >
            {job.title}
          </h3>
          <p className="mt-0.5 flex items-center gap-1 text-xs text-[var(--color-text-secondary)]" style={font}>
            {job.company.name}
            {job.company.verificationLevel !== 'none' && (
              <VerificationBadge level={job.company.verificationLevel} size="sm" />
            )}
          </p>
        </div>
        {isAuthenticated && (
          <SaveJobButton jobId={job.id} />
        )}
      </div>

      {/* Budget */}
      <p className="mt-3 text-sm font-medium text-[var(--color-text-primary)]" style={font}>
        {formatBudgetRange(job.budgetType, job.budgetMin, job.budgetMax)}
      </p>

      {/* Meta row */}
      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-[var(--color-text-muted)]" style={font}>
        <JobTypeBadge type={job.jobType} />
        <span className="inline-flex items-center gap-1">
          <MapPin className="h-3 w-3" />
          {locationLabel}
        </span>
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {formatTimeAgo(job.publishedAt)}
        </span>
        {job.deadline && (
          <DeadlineBadge deadline={job.deadline} variant="inline" />
        )}
      </div>

      {/* Specializations */}
      {job.specializations.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {job.specializations.slice(0, 3).map((spec) => (
            <span
              key={spec}
              className="rounded-full bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[11px] text-[var(--color-text-secondary)]"
              style={font}
            >
              {getSpecLabel(spec)}
            </span>
          ))}
          {job.specializations.length > 3 && (
            <span
              className="rounded-full bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[11px] text-[var(--color-text-muted)]"
              style={font}
            >
              +{job.specializations.length - 3}
            </span>
          )}
        </div>
      )}
    </Link>
  )
}
